import { useState } from "react";
import { Popover } from "@mui/material";
import { useTranslation } from "@i18n";
import { RECENT_EMOJIS_STORAGE_KEY } from "../portalChat.constants";

/** Quick-reaction row shown on hover (PRD: open emoji set; these are shortcuts into it). */
export const QUICK_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

const EMOJI_GROUPS: { label: string; emojis: string[] }[] = [
  {
    label: "Smileys",
    emojis: [
      "😀",
      "😃",
      "😄",
      "😁",
      "😆",
      "😅",
      "😂",
      "🤣",
      "😊",
      "😇",
      "🙂",
      "😉",
      "😍",
      "🥰",
      "😘",
      "😗",
      "😜",
      "🤪",
      "🤔",
      "🤨",
      "😐",
      "😑",
      "😶",
      "🙄",
      "😏",
      "😴",
      "😪",
      "🤤",
      "😷",
      "🤒",
      "🤕",
      "🥳",
      "😎",
      "🤓",
      "🧐",
      "😕",
      "😟",
      "🙁",
      "😮",
      "😯",
      "😲",
      "😳",
      "🥺",
      "😢",
      "😭",
      "😤",
      "😠",
      "😡",
    ],
  },
  {
    label: "Gestures",
    emojis: [
      "👍",
      "👎",
      "👌",
      "✌️",
      "🤞",
      "🤟",
      "🤘",
      "👏",
      "🙌",
      "👐",
      "🤝",
      "🙏",
      "💪",
      "👊",
      "✊",
      "🤚",
      "👋",
      "🤙",
      "👈",
      "👉",
      "👆",
      "👇",
      "☝️",
      "✋",
    ],
  },
  {
    label: "Objects",
    emojis: [
      "❤️",
      "🧡",
      "💛",
      "💚",
      "💙",
      "💜",
      "🖤",
      "💔",
      "💯",
      "🔥",
      "⭐",
      "🌟",
      "✨",
      "⚡",
      "☀️",
      "🌈",
      "🎉",
      "🎊",
      "🏆",
      "🥇",
      "📌",
      "📎",
      "📁",
      "📄",
      "🗓️",
      "⏰",
      "🚀",
      "🏗️",
      "🏢",
      "🔧",
      "🛠️",
      "⚙️",
    ],
  },
];

type EmojiPickerProps = {
  readonly anchorEl: HTMLElement | null;
  readonly onClose: () => void;
  readonly onPick: (emoji: string) => void;
};

/** Compact dependency-free emoji picker (searchless grid over curated groups). */
export function EmojiPicker({ anchorEl, onClose, onPick }: EmojiPickerProps) {
  const { t } = useTranslation();
  const groupLabel = (label: string) =>
    label === "Smileys" ? t("Smileys") : label === "Gestures" ? t("Gestures") : t("Objects");
  const [recent, setRecent] = useState<string[]>(() => {
    try {
      const parsed: unknown = JSON.parse(localStorage.getItem(RECENT_EMOJIS_STORAGE_KEY) ?? "[]");
      return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === "string") : [];
    } catch {
      return [];
    }
  });

  const pick = (emoji: string) => {
    const nextRecent = [emoji, ...recent.filter((e) => e !== emoji)].slice(0, 12);
    setRecent(nextRecent);
    try {
      localStorage.setItem(RECENT_EMOJIS_STORAGE_KEY, JSON.stringify(nextRecent));
    } catch {
      // localStorage may be unavailable; recents are cosmetic.
    }
    onPick(emoji);
    onClose();
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      transformOrigin={{ vertical: "bottom", horizontal: "center" }}
      sx={{ zIndex: 1400 }}
    >
      <div className="max-h-72 w-72 overflow-y-auto p-2">
        {recent.length > 0 && (
          <div>
            <div className="px-1 pb-1 text-xs font-semibold text-gray-400">{t("Recently used")}</div>
            <div className="grid grid-cols-8">
              {recent.map((emoji) => (
                <button
                  key={`recent-${emoji}`}
                  type="button"
                  className="rounded p-1 text-lg hover:bg-[#f0f5f9]"
                  onClick={() => pick(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        )}
        {EMOJI_GROUPS.map((group) => (
          <div key={group.label}>
            <div className="px-1 pb-1 pt-2 text-xs font-semibold text-gray-400">{groupLabel(group.label)}</div>
            <div className="grid grid-cols-8">
              {group.emojis.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className="rounded p-1 text-lg hover:bg-[#f0f5f9]"
                  onClick={() => pick(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Popover>
  );
}
